import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Connectivity,
    currentConnectivity,
    getSpeechRecognitionConstructor,
    prefersSingleUtteranceSessions,
    SpeechRecognizer,
    SpeechRecognizerErrorEvent,
    SpeechRecognizerResultEvent,
} from "util/speech-recognition";

export type DictationStatus = "idle" | "listening";

export type DictationError =
    | "permission-denied"
    | "no-microphone"
    | "offline"
    | "speech-service-unreachable"
    | "no-speech"
    | "unavailable";

export type DictationEndReason = "user" | "silence" | "error" | "timeout" | "background";

export interface UseDictationParams {
    lang: string;
    onFinal: (text: string) => void;
    onInterim: (text: string) => void;
    onEnd: (reason: DictationEndReason) => void;
}

export interface Dictation {
    isSupported: boolean;
    status: DictationStatus;
    error: DictationError | null;
    start: () => void;
    stop: () => void;
    abort: () => void;
}

interface DictationSession {
    recognizer: SpeechRecognizer;
    finalizedCount: number;
    lastInterim: string;
    heardSpeech: boolean;
    abortedByBrowser: boolean;
    fatalError: DictationError | null;
}

type RecognizerErrorOutcome =
    | { kind: "aborted" }
    | { kind: "silence" }
    | { kind: "fatal"; error: DictationError };

const maxSilentSessions = 3;
export const maxDictationSessionMs = 5 * 60 * 1000;

const classifyRecognizerError = (
    code: string,
    connectivity: Connectivity,
): RecognizerErrorOutcome => {
    switch (code) {
        case "aborted":
            return { kind: "aborted" };
        case "no-speech":
            return { kind: "silence" };
        case "not-allowed":
        case "service-not-allowed":
            return { kind: "fatal", error: "permission-denied" };
        case "audio-capture":
            return { kind: "fatal", error: "no-microphone" };
        case "network":
            return {
                kind: "fatal",
                error:
                    connectivity === "offline" ? "offline" : "speech-service-unreachable",
            };
        default:
            return { kind: "fatal", error: "unavailable" };
    }
};

export const useDictation = (params: UseDictationParams): Dictation => {
    const [isSupported] = useState(() => getSpeechRecognitionConstructor() !== null);
    const [status, setStatus] = useState<DictationStatus>("idle");
    const [error, setError] = useState<DictationError | null>(null);
    const paramsRef = useRef(params);
    const sessionRef = useRef<DictationSession | null>(null);
    const wantsListeningRef = useRef(false);
    const stopReasonRef = useRef<DictationEndReason>("user");
    const silentSessionsRef = useRef(0);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const restartRef = useRef<() => void>(() => undefined);

    useEffect(() => {
        paramsRef.current = params;
    });

    const clearSessionTimeout = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const finish = useCallback(
        (reason: DictationEndReason) => {
            clearSessionTimeout();
            wantsListeningRef.current = false;
            setStatus("idle");
            paramsRef.current.onEnd(reason);
        },
        [clearSessionTimeout],
    );

    const handleResult = useCallback(
        (session: DictationSession, event: SpeechRecognizerResultEvent) => {
            session.heardSpeech = true;
            silentSessionsRef.current = 0;
            let interim = "";
            for (let index = 0; index < event.results.length; index++) {
                const result = event.results[index];
                if (!result.isFinal) {
                    interim += result[0].transcript;
                } else if (index >= session.finalizedCount) {
                    session.finalizedCount = index + 1;
                    paramsRef.current.onFinal(result[0].transcript);
                }
            }
            if (interim !== session.lastInterim) {
                session.lastInterim = interim;
                paramsRef.current.onInterim(interim);
            }
        },
        [],
    );

    const handleError = useCallback(
        (session: DictationSession, event: SpeechRecognizerErrorEvent) => {
            const outcome = classifyRecognizerError(event.error, currentConnectivity());
            switch (outcome.kind) {
                case "aborted":
                    session.abortedByBrowser = true;
                    return;
                case "silence":
                    return;
                case "fatal":
                    session.fatalError = outcome.error;
                    setError(outcome.error);
                    return;
                default: {
                    const exhaustiveCheck: never = outcome;
                    throw new Error(
                        `Unhandled recognizer error outcome ${exhaustiveCheck}`,
                    );
                }
            }
        },
        [],
    );

    const handleEnd = useCallback(
        (session: DictationSession) => {
            if (sessionRef.current !== session) {
                return;
            }
            sessionRef.current = null;
            if (session.fatalError) {
                finish("error");
            } else if (!wantsListeningRef.current) {
                finish(stopReasonRef.current);
            } else if (session.abortedByBrowser) {
                finish("user");
            } else if (
                !session.heardSpeech &&
                ++silentSessionsRef.current >= maxSilentSessions
            ) {
                setError("no-speech");
                finish("silence");
            } else {
                restartRef.current();
            }
        },
        [finish],
    );

    const startSession = useCallback(() => {
        const Recognizer = getSpeechRecognitionConstructor();
        if (!Recognizer) {
            return;
        }
        const recognizer = new Recognizer();
        recognizer.lang = paramsRef.current.lang;
        recognizer.interimResults = true;
        recognizer.continuous = !prefersSingleUtteranceSessions(navigator.userAgent);
        const session: DictationSession = {
            recognizer,
            finalizedCount: 0,
            lastInterim: "",
            heardSpeech: false,
            abortedByBrowser: false,
            fatalError: null,
        };
        recognizer.onresult = event => handleResult(session, event);
        recognizer.onerror = event => handleError(session, event);
        recognizer.onend = () => handleEnd(session);
        sessionRef.current = session;
        try {
            recognizer.start();
        } catch {
            sessionRef.current = null;
            setError("unavailable");
            finish("error");
        }
    }, [finish, handleEnd, handleError, handleResult]);

    useEffect(() => {
        restartRef.current = startSession;
    }, [startSession]);

    const stopWith = useCallback((reason: DictationEndReason) => {
        const session = sessionRef.current;
        if (!session) {
            return;
        }
        wantsListeningRef.current = false;
        stopReasonRef.current = reason;
        session.recognizer.stop();
    }, []);

    const start = useCallback(() => {
        if (!isSupported || wantsListeningRef.current) {
            return;
        }
        setError(null);
        silentSessionsRef.current = 0;
        wantsListeningRef.current = true;
        stopReasonRef.current = "user";
        setStatus("listening");
        timeoutRef.current = setTimeout(() => stopWith("timeout"), maxDictationSessionMs);
        startSession();
    }, [isSupported, startSession, stopWith]);

    const stop = useCallback(() => stopWith("user"), [stopWith]);

    const abort = useCallback(() => {
        const session = sessionRef.current;
        if (!session) {
            return;
        }
        sessionRef.current = null;
        session.recognizer.abort();
        finish("user");
    }, [finish]);

    useEffect(() => {
        if (status !== "listening") {
            return undefined;
        }
        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                stopWith("background");
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () =>
            document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [status, stopWith]);

    useEffect(
        () => () => {
            clearSessionTimeout();
            const session = sessionRef.current;
            if (session) {
                sessionRef.current = null;
                session.recognizer.abort();
            }
        },
        [clearSessionTimeout],
    );

    return useMemo(
        () => ({ isSupported, status, error, start, stop, abort }),
        [isSupported, status, error, start, stop, abort],
    );
};
